<div class="issue-report-modal" role="presentation">
  <div class="issue-report-modal__backdrop" aria-hidden="true"></div>

  <section
    #dialogPanel
    class="issue-report-modal__panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="issue-report-title"
    aria-describedby="issue-report-subtitle"
  >
    <header class="issue-report-modal__header">
      <div class="issue-report-modal__heading">
        <span
          class="issue-report-modal__icon fa-solid fa-triangle-exclamation"
          aria-hidden="true"
        ></span>
        <div>
          <h2 id="issue-report-title" class="issue-report-modal__title">
            {{ i18n.t('issueReport.title') }}
          </h2>
          <p id="issue-report-subtitle" class="issue-report-modal__subtitle">
            {{ i18n.t('issueReport.subtitle') }}
          </p>
        </div>
      </div>

      <button
        class="app-icon-button issue-report-modal__close"
        type="button"
        [attr.aria-label]="i18n.t('common.actions.close')"
        [disabled]="isSubmitting()"
        (click)="close()"
      >
        <span class="fa-solid fa-xmark" aria-hidden="true"></span>
      </button>
    </header>

    <form class="issue-report-modal__form" [formGroup]="form" (ngSubmit)="submit()">
      <div class="issue-report-modal__form-grid">
        <label
          class="app-field issue-report-modal__field-full"
          [class.app-field--invalid]="form.controls.title.invalid && form.controls.title.touched"
        >
          <span class="app-field__label">{{ i18n.t('issueReport.fields.title') }}</span>
          <input
            #titleInput
            type="text"
            formControlName="title"
            maxlength="255"
            [placeholder]="i18n.t('issueReport.placeholders.title')"
          />
          @if (form.controls.title.touched && form.controls.title.hasError('required')) {
            <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
          } @else if (form.controls.title.touched && form.controls.title.hasError('maxlength')) {
            <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.titleTooLong') }}</span>
          }
        </label>

        <label
          class="app-field issue-report-modal__field-full"
          [class.app-field--invalid]="
            form.controls.description.invalid && form.controls.description.touched
          "
        >
          <span class="app-field__label">{{ i18n.t('issueReport.fields.description') }}</span>
          <textarea
            class="app-textarea"
            formControlName="description"
            rows="4"
            [placeholder]="i18n.t('issueReport.placeholders.description')"
          ></textarea>
          @if (
            form.controls.description.touched && form.controls.description.hasError('required')
          ) {
            <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
          }
        </label>

        <label
          class="app-field"
          [class.app-field--invalid]="
            form.controls.priority.invalid && form.controls.priority.touched
          "
        >
          <span class="app-field__label">{{ i18n.t('issueReport.fields.priority') }}</span>
          <select
            formControlName="priority"
            [title]="priorityDueHint(form.controls.priority.value)"
          >
            @for (priority of priorities; track priority.value) {
              <option [value]="priority.value" [title]="priorityDueHint(priority.value)">
                {{ i18n.t(priority.labelKey) }}
              </option>
            }
          </select>
        </label>

        <label
          class="app-field"
          [class.app-field--invalid]="
            form.controls.dueDate.invalid && form.controls.dueDate.touched
          "
        >
          <span class="app-field__label">{{ i18n.t('issueReport.fields.dueDate') }}</span>
          <input type="datetime-local" formControlName="dueDate" />
          @if (form.controls.dueDate.touched && form.controls.dueDate.hasError('required')) {
            <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
          }
        </label>

        <label
          class="app-field"
          [class.app-field--invalid]="
            form.controls.locationId.invalid && form.controls.locationId.touched
          "
        >
          <span class="app-field__label">{{ i18n.t('issueReport.fields.location') }}</span>
          <select formControlName="locationId" [disabled]="isLoadingOptions()">
            <option value="">{{ i18n.t('issueReport.placeholders.location') }}</option>
            @for (location of locations(); track location.id) {
              <option [value]="location.id">{{ location.name }}</option>
            }
          </select>
          @if (form.controls.locationId.touched && form.controls.locationId.hasError('required')) {
            <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
          }
        </label>

        <label
          class="app-field"
          [class.app-field--invalid]="
            form.controls.assignedRoleId.invalid && form.controls.assignedRoleId.touched
          "
        >
          <span class="app-field__label">{{ i18n.t('issueReport.fields.assignedRole') }}</span>
          <select formControlName="assignedRoleId" [disabled]="isLoadingOptions()">
            <option value="">{{ i18n.t('issueReport.placeholders.role') }}</option>
            @for (role of roles(); track role.id) {
              <option [value]="role.id">{{ role.name }}</option>
            }
          </select>
          @if (
            form.controls.assignedRoleId.touched &&
            form.controls.assignedRoleId.hasError('required')
          ) {
            <span class="app-field__error"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('issueReport.errors.required') }}</span>
          }
        </label>
      </div>

      @if (!isLoadingOptions() && optionsLoadError(); as error) {
        <div class="form-message" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      } @else if (!isLoadingOptions() && !hasLocations()) {
        <div class="app-empty-state issue-report-modal__options-state"><span class="app-message__icon app-message__icon--warning fa-solid fa-triangle-exclamation" aria-hidden="true"></span>
          {{ i18n.t('issueReport.empty.locations') }}
        </div>
      } @else if (!isLoadingOptions() && !hasRoles()) {
        <div class="app-empty-state issue-report-modal__options-state"><span class="app-message__icon app-message__icon--warning fa-solid fa-triangle-exclamation" aria-hidden="true"></span>
          {{ i18n.t('issueReport.empty.roles') }}
        </div>
      }

      <section class="app-panel app-panel--nested issue-report-modal__attachments">
        <div class="issue-report-modal__attachments-header">
          <div>
            <h3 class="app-panel__title">{{ i18n.t('issueReport.attachments.title') }}</h3>
            <p class="app-panel__meta">{{ i18n.t('issueReport.attachments.hint') }}</p>
          </div>

          <label
            class="secondary-button secondary-button--compact secondary-button--indigo app-attachment-upload issue-report-modal__upload"
          >
            <span class="fa-solid fa-camera" aria-hidden="true"></span>
            {{ i18n.t('issueReport.attachments.add') }}
            <input type="file" accept="image/*" multiple (change)="addAttachments($event)" />
          </label>
        </div>

        @if (attachments().length) {
          <ul class="app-attachment-list issue-report-modal__attachment-list">
            @for (attachment of attachments(); track $index) {
              <li class="app-attachment-item issue-report-modal__attachment">
                <span class="fa-solid fa-image" aria-hidden="true"></span>
                <span class="app-attachment-name issue-report-modal__attachment-name">{{
                  attachment.name
                }}</span>
                <button
                  class="app-icon-button app-attachment-remove issue-report-modal__remove-attachment"
                  type="button"
                  [attr.aria-label]="
                    i18n.t('issueReport.attachments.remove') + ': ' + attachment.name
                  "
                  (click)="removeAttachment($index)"
                >
                  <span class="fa-solid fa-trash" aria-hidden="true"></span>
                </button>
              </li>
            }
          </ul>
        }

        @if (attachmentError(); as error) {
          <div class="form-message" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
        }
      </section>

      @if (submitError(); as error) {
        <div class="form-message" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }

      <footer class="issue-report-modal__actions">
        <button
          class="secondary-button secondary-button--indigo"
          type="button"
          [disabled]="isSubmitting()"
          (click)="close()"
        >
          {{ i18n.t('issueReport.actions.cancel') }}
        </button>
        <button
          class="primary-button"
          type="submit"
          [disabled]="isSubmitting() || isLoadingOptions() || !hasLocations() || !hasRoles()"
        >
          {{
            isSubmitting()
              ? i18n.t('issueReport.actions.submitting')
              : i18n.t('issueReport.actions.submit')
          }}
        </button>
      </footer>
    </form>
  </section>
</div>
